import type jsPDF from 'jspdf';
import { BrandPdf, type PdfTableSpec, type Rgb } from '@/lib/pdf/brandPdf';
import type { PdfAssets } from '@/lib/pdf/pdfAssets';

/** Lays out the Call Cost Report PDF: header, summary tiles, cost split and tables. */

export { PDF_COLORS } from '@/lib/pdf/brandPdf';
export type { PdfCell, PdfTableSpec, PdfValue, Rgb } from '@/lib/pdf/brandPdf';

export interface CallCostPdfSpec {
  orientation: 'portrait' | 'landscape';
  /** Fonts and logo; without them the PDF falls back to Helvetica and a text wordmark. */
  assets?: PdfAssets;
  customerName?: string;
  heading: string;
  period: string;
  generated: string;
  chips: string[];
  /** The featured card is filled with the accent; `color` is the dot before the others' labels. */
  cards: Array<{ label: string; value: string; detail: string; color?: Rgb; featured?: boolean }>;
  /** Cost split bar; left out when nothing was charged. */
  split?: Array<{ label: string; amount: number; display: string; color: Rgb }>;
  note?: string;
  tables: PdfTableSpec[];
}

export function buildCallCostPdf(spec: CallCostPdfSpec): jsPDF {
  const doc = new BrandPdf({
    orientation: spec.orientation,
    assets: spec.assets,
    reportName: 'Call cost report',
    title: spec.heading,
    subtitle: spec.period,
    customerName: spec.customerName,
    generated: spec.generated,
    chips: spec.chips,
  });
  doc.statTiles(
    spec.cards.map((card) => ({
      label: card.label,
      value: card.value,
      caption: card.detail,
      featured: card.featured,
      dot: card.featured ? undefined : card.color,
    }))
  );
  if (spec.split) doc.splitBar('Cost split', spec.split);
  if (spec.note) doc.note(spec.note);
  for (const table of spec.tables) doc.table(table);
  return doc.finish();
}
